import { getBaseRole } from '@/helpers/customRoles';
import Sidebar from '@common/Sidebar';
import Header from '@common/Header';
import BottomMenu from '@common/BottomMenu';
import classNames from "classnames";
import s from './Layout.css';
import { useIsMinimizedMenu } from '@/hooks/useIsMinimizedMenu';
import { useIsCurrentPage } from '@/hooks/useIsCurrentPage';
import { ChatProvider } from '@common/Chat/ChatContext';
import { useChatState } from '@common/Chat/useChatState';
import { useLocation } from "react-router-dom";
import { CrmIntegrationChatAvailabilityProvider } from '@/hooks/CrmIntegrationChatAvailabilityProvider';
import { IntegrationChatDockProvider } from '@common/IntegrationChatDock/IntegrationChatDockContext';
import IntegrationChatDockPanel from '@common/IntegrationChatDock/IntegrationChatDockPanel';
import IntegrationChatDockFloatingActions from '@common/IntegrationChatDock/IntegrationChatDockFloatingActions';
import IntegrationChatDockLauncherRealtimeBridge from '@common/IntegrationChatDock/IntegrationChatDockLauncherRealtimeBridge';
import WebSocketConnectionAlertBridge from '@common/WebSocketConnectionAlertBridge/WebSocketConnectionAlertBridge';
import { useIsChatEnabled } from '@common/Chat/hooks/useIsChatEnabled';
import DailyLoginRewardModal from '@common/DailyLoginRewardModal/DailyLoginRewardModal';
import RatingChangeNotifications from '@pages/Profile/MyRating/RatingChangeNotifications';
import Preloader from '@UI/Preloader';
// Обучение для платформы репетиторов временно отключено.
// import PlatformTrainingOverlay from '@common/PlatformTraining/PlatformTrainingOverlay';
import { useDispatch, useSelector } from 'react-redux';
import { selectProfile } from '@store/profile.reducer';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIsDesktopHighZoom } from '@/hooks/useIsDesktopHighZoom';
import LicenseExpiredModal from '@common/LicenseExpiredModal/LicenseExpiredModal';
import SupportSurveyModal from '@common/SupportSurveyModal/SupportSurveyModal';
import PlatformSupport from '@common/PlatformSupport/PlatformSupport';
import ReportsNavigation from '@pages/Admin/Reports';

import { actionMinimizedMenu } from '@store/menu.reducer';

import {
  endPublicHomeworkSession,
  hasPublicHomeworkSession,
  isPublicHomeworkPath,
} from '@/helpers/publicHomeworkSession';
import { AuthService } from '@/services/AuthService';
import { isLessonChatsRoute } from '@/helpers/lessonChats';


const Layout = ({ children }) => {
  const dispatch = useDispatch();
  const isMinimizedMenu = useIsMinimizedMenu();
  const chatState = useChatState();
  const profile = useSelector(selectProfile);
  const location = useLocation();
  const isChatsPage = location.pathname.toLowerCase() === '/chats';
  const isAllUsersPage = [
    '/users/colleges',
    '/users/students',
    '/users/parents',
  ].some((path) => location.pathname.toLowerCase().startsWith(path));
  const isReportsPage = location.pathname.toLowerCase().startsWith('/reports');
  const isJournalPage = location.pathname.toLowerCase().startsWith('/v2/journal');
  const isManageGroupsPage = location.pathname.toLowerCase().startsWith('/manage/groups');
  const isLessonPage = isLessonChatsRoute({
    pathname: location.pathname,
    lessonId: new URLSearchParams(location.search).get('lesson'),
  });
  const isPublicHomeworkSession = hasPublicHomeworkSession();
  const isPublicHomeworkQuery = new URLSearchParams(location.search).get('public') === '1';
  const shouldUsePublicHomeworkLayout =
    (isPublicHomeworkSession || isPublicHomeworkQuery) &&
    isPublicHomeworkPath(location.pathname);

  const isCrmKanbanPage = useIsCurrentPage('crmKanban');
  const isTaskTrackerPage = useIsCurrentPage('taskTracker');
  const isHelpdeskPage = location.pathname.toLowerCase().startsWith('/helpdesk');
  const shouldHideHeader =
    isCrmKanbanPage || isTaskTrackerPage || shouldUsePublicHomeworkLayout || isHelpdeskPage;

  const shouldUseFullWidthContent = shouldHideHeader;
  const isDesktopHighZoom = useIsDesktopHighZoom();
  const [isHighZoomHeaderVisible, setIsHighZoomHeaderVisible] = useState(false);
  const lastScrollTopRef = useRef(0);
  const lastScrollTargetRef = useRef(null);
  const hideHeaderTimerRef = useRef(null);

  useEffect(() => {
    if (!isHelpdeskPage) return undefined;

    dispatch(actionMinimizedMenu());
  }, [dispatch, isHelpdeskPage]);

  const showHighZoomHeader = useCallback((withAutoHide = false) => {
    setIsHighZoomHeaderVisible(true);

    if (hideHeaderTimerRef.current) {
      clearTimeout(hideHeaderTimerRef.current);
    }

    if (withAutoHide) {
      hideHeaderTimerRef.current = setTimeout(() => {
        setIsHighZoomHeaderVisible(false);
      }, 2500);
    }
  }, []);

  // Скрытый ученик публичной ссылки существует только ради самой работы: шаг за
  // пределы её страницы завершает сессию, иначе человек попадёт в кабинет,
  // о котором он знать не должен. logout чистит localStorage вместе с меткой
  useEffect(() => {
    if (
      isPublicHomeworkSession &&
      !isPublicHomeworkPath(location.pathname) &&
      location.pathname !== '/home-work/work-link'
    ) {
      // метку снимаем до logout: если он упадёт, страница не зациклится на редиректе
      endPublicHomeworkSession();
      Promise.resolve(AuthService.logout()).finally(() => {
        window.location.assign('/auth');
      });
    }
  }, [isPublicHomeworkSession, location.pathname]);

  useEffect(() => {
    if (!isDesktopHighZoom || shouldHideHeader) {
      setIsHighZoomHeaderVisible(false);
      return undefined;
    }

    const getScrollData = (event) => {
      const target = event?.target;

      if (target && target !== document && target !== window) {
        return {
          target,
          scrollTop: target.scrollTop || 0,
        };
      }

      return {
        target: window,
        scrollTop: window.pageYOffset || document.documentElement.scrollTop || 0,
      };
    };

    const handleScroll = (event) => {
      const { target, scrollTop: currentScrollTop } = getScrollData(event);

      if (lastScrollTargetRef.current !== target) {
        lastScrollTargetRef.current = target;
        lastScrollTopRef.current = Math.max(currentScrollTop, 0);
      }

      if (currentScrollTop < lastScrollTopRef.current - 4) {
        showHighZoomHeader(true);
      }

      if (currentScrollTop > lastScrollTopRef.current + 4) {
        setIsHighZoomHeaderVisible(false);
      }

      lastScrollTopRef.current = Math.max(currentScrollTop, 0);
    };

    const handleWheel = (event) => {
      if (event.deltaY > 4) {
        setIsHighZoomHeaderVisible(false);
      }

      if (event.deltaY < -4) {
        showHighZoomHeader(true);
      }
    };

    const handleKeyDown = (event) => {
      const downKeys = ['ArrowDown', 'PageDown', 'End', ' '];
      const upKeys = ['ArrowUp', 'PageUp', 'Home'];

      if (downKeys.includes(event.key)) {
        setIsHighZoomHeaderVisible(false);
      }

      if (upKeys.includes(event.key)) {
        showHighZoomHeader(true);
      }
    };

    const handleMouseMove = (event) => {
      if (event.clientY <= 18) {
        showHighZoomHeader(false);
        return;
      }

      const headerElement = document.querySelector('.app-header');
      const headerBottom = headerElement?.getBoundingClientRect?.().bottom || 0;

      if (
        isHighZoomHeaderVisible &&
        headerBottom > 0 &&
        event.clientY > headerBottom + 16
      ) {
        setIsHighZoomHeaderVisible(false);
      }
    };

    lastScrollTopRef.current =
      window.pageYOffset || document.documentElement.scrollTop || 0;
    lastScrollTargetRef.current = window;

    document.addEventListener('scroll', handleScroll, { passive: true, capture: true });
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('wheel', handleWheel, { passive: true });
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      document.removeEventListener('scroll', handleScroll, { capture: true });
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('mousemove', handleMouseMove);
      if (hideHeaderTimerRef.current) {
        clearTimeout(hideHeaderTimerRef.current);
      }
    };
  }, [
    isDesktopHighZoom,
    isHighZoomHeaderVisible,
    shouldHideHeader,
    showHighZoomHeader,
  ]);

  return (
    <CrmIntegrationChatAvailabilityProvider>
    <IntegrationChatDockProvider>
    <IntegrationChatDockLauncherRealtimeBridge />
    <WebSocketConnectionAlertBridge />
    <ChatProvider changePage={chatState.changePage}>
      <PlatformSupport enabled={!shouldUsePublicHomeworkLayout}>
      <div
        className={classNames(
          'app-wrapper',
          { 'app-wrapper-menu-minimized': isMinimizedMenu },
          { 'app-wrapper-reports': isReportsPage },
          { 'app-wrapper-journal': isJournalPage },
          { 'app-wrapper-users': isAllUsersPage },
          { 'app-wrapper-manage-groups': isManageGroupsPage },
          { 'app-wrapper-full-content': shouldUseFullWidthContent },
          { 'app-wrapper-public-homework': shouldUsePublicHomeworkLayout },
          { 'app-wrapper-high-zoom': isDesktopHighZoom && !shouldHideHeader },
          {
            'app-wrapper-high-zoom-header-visible':
              isDesktopHighZoom && isHighZoomHeaderVisible && !shouldHideHeader,
          },
          !shouldUseFullWidthContent && s.sideWrapper,
        )}
      >
        {isDesktopHighZoom && !shouldHideHeader && (
          <div
            className={s.highZoomHeaderTrigger}
            onMouseEnter={() => showHighZoomHeader(false)}
          />
        )}
        {!shouldUsePublicHomeworkLayout && <Sidebar />}
        {!shouldUsePublicHomeworkLayout && <ReportsNavigation />}
        {/* {!shouldUsePublicHomeworkLayout && <PlatformTrainingOverlay />} */}
        {!shouldHideHeader && <Header />}
        <main
          className="page-content google-translation-route-loader"
          data-google-translation-loader
        >
          <Preloader fullScreen="center" />
        </main>
        {!shouldUsePublicHomeworkLayout && (
          <>
            <LicenseExpiredModal profile={profile} />
            <SupportSurveyModal profile={profile} />
            <DailyLoginRewardModal />
            <RatingChangeNotifications
              enabled={getBaseRole(profile?.role) === 'children'}
              profileId={profile?.id}
            />
          </>
        )}
        {children}
        {!shouldUsePublicHomeworkLayout && <BottomMenu />}
        {!shouldUsePublicHomeworkLayout && !isChatsPage && !isHelpdeskPage && (
          <>
            {!isLessonPage && <IntegrationChatDockPanel />}
            <IntegrationChatDockFloatingActions chatState={chatState} />
          </>
        )}
      </div>
      </PlatformSupport>
    </ChatProvider>
    </IntegrationChatDockProvider>
    </CrmIntegrationChatAvailabilityProvider>
  );
};

export default Layout;
